/**
 * Economics dashboard model.
 *
 * Only stored deal values, stored cash-flow schedules, and values supplied by
 * the calculation service through `get` enter this model. Missing series stay
 * empty; downside/upside cases and sensitivity ranges are never fabricated.
 */

import type { RawDealFile } from '@/services/dealFile'
import type { LoanActivityEvent } from '@/pages/pipeline-intelligence/types'
import { asNumber, formatAmount, formatDate, formatPct, formatPoints } from './format'
import { isLodging, keysLabel } from './lodging'
import { buildCoverageRows } from './economicsCoverage'
import { ltvWithImplied } from './valueBasis'
import {
  amortizationLabel,
  bpsLabel,
  monthsLabel,
  positionLabel,
  productField,
  rateText,
  SENIOR_NOT_STATED,
  seniorAheadUnstated,
  spreadLabel,
} from './dealTerms'
import { heldFor, heldLine, heldRevisions, type HeldRevision } from './heldRevisions'

export type CashFlowLine = {
  label: string
  value: number | undefined
  emphasize?: boolean
  expense?: boolean
  /** The catalogue field the line shows, where it is one — so its value
   *  state (a debt service back-solved from NOI / DSCR is Derived) can be
   *  shown beside it. */
  fieldName?: string
  /** Why the figure is an assumption, not data (a net cash flow computed
   *  with an unstated reserve taken as $0) — shown as Suggested (ECON-14). */
  suggested?: string
  /** The formula of a figure this page computes from the deal's own lines
   *  (EGI, net cash flow) — shown as Derived, never as a statement (C05). */
  derived?: string
  /** The catalogue fields a computed line is made of, which the unlocked
   *  dossier offers in place of the line itself (R1: a person corrects the
   *  reserve or the debt service, never the net cash flow they produce). */
  inputs?: string[]
}

export type ExpenseSlice = { name: string; amount: number }

/** One of a hotel's operating statistics, printed as the record states it. */
export type HotelStat = { label: string; value: string; fieldName: string }

export type ProjectionRow = {
  year: string
  egi: number | undefined
  opex: number | undefined
  noi: number | undefined
  cashFlow: number | undefined
  dscr: number | undefined
}

export type TrendPoint = {
  label: string
  egi?: number
  opex?: number
  noi?: number
}

export type DscrPoint = { label: string; dscr: number }

/** Where a stored schedule was read from, when it was read from a model.
 *  The same attribution the KPI cards carry: the client screens on the
 *  sponsor's pro forma and runs its own, so a projection shown without saying
 *  whose it is reads as the desk's own underwriting. */
export type ScheduleSource = {
  workbook: string
  sheet: string
  cadence: string
  lines: string[]
  cells: Record<string, Record<string, string>>
}

/** One line of the sponsor's operating statement, in their own words.
 *  `key` is set only for the handful this app has a canonical name for; the
 *  rest — GTL/(LTL), RUBS, Absorption & Turnover Vacancy — are the detail an
 *  underwriter opens the model to read, and dropping them would show a
 *  seven-line summary as the statement. */
export type StatementLine = {
  label: string
  key: string | null
  values: Array<number | null>
  cells: string[]
}

export type SponsorStatement = {
  workbook: string
  sheet: string
  cadence: string
  periods: string[]
  lines: StatementLine[]
  truncated?: number
}

export type UwColumnId = 'actuals' | 'sponsor' | 'arep'

export type UwGridColumn = { id: UwColumnId; label: string }

function asUwColumnId(value: string | null | undefined): UwColumnId | null {
  switch (value) {
    case 'actuals':
    case 'sponsor':
    case 'arep':
      return value
    default:
      return null
  }
}

export type UwGridLine = {
  label: string
  key: string | null
  values: Partial<Record<UwColumnId, number | null>>
  cells: Partial<Record<UwColumnId, string>>
}

export type UwGrid = {
  workbook: string
  sheet: string
  columns: UwGridColumn[]
  lines: UwGridLine[]
}

export type ScenarioId = 'base' | 'downside' | 'upside'

/** What a scenario's value is a value of. `unstated`: the scenario gave a
 *  bare value (often an exit-cap value), so no basis is claimed for it. */
export type ScenarioValueBasis = 'as_is' | 'stabilized' | 'unstated'

export type ScenarioMetrics = {
  id: ScenarioId
  label: string
  /** Stabilized NOI only — never an unqualified or in-place one (C09). */
  noi: number | undefined
  value: number | undefined
  valueBasis?: ScenarioValueBasis
  ltv: number | undefined
  /** Stabilized DSCR only. */
  dscr: number | undefined
  /** A NOI / DSCR the scenario states without saying in-place or stabilized.
   *  Shown under its own row, never borrowed into the stabilized one. */
  noiUnqualified?: number
  dscrUnqualified?: number
  cashOnCash: number | undefined
  irr: number | undefined
  note: string | null
  /** The deal's loan amount, carried only on the Recorded Case, whose value
   *  and LTV are the deal's own; a saved scenario's LTV stands as stated. */
  loanAmount?: number
}

/** A scenario's LTV as the dossier shows it: on the Recorded Case, the LTV the
 *  deal's loan and value imply beside a stated one that differs (R4). 45 Park
 *  Ave's row read a bare "58.00%" beside a $44.92M component value while every
 *  tile read "58.00% · 69.0% derived". */
export function scenarioLtvText(scenario: ScenarioMetrics | null | undefined): string {
  return ltvWithImplied(scenario?.ltv, scenario?.loanAmount, scenario?.value)
}

export type FinancingRow = {
  label: string
  value: unknown
  /** `points`: a catalogue percent held in custom_fields, on the 0-100 scale. */
  format: 'money' | 'percent' | 'points' | 'months' | 'text' | 'rate' | 'bps' | 'spread' | 'date'
  /** The catalogue field the value was read from, so the row carries that
   *  field's value state (Derived, Suggested, Confirmed) while locked (C05).
   *  For a row with fallbacks, the one that held the value. */
  fieldName?: string
  /** A value held off the deal for this row (a later email's, or the
   *  lender's own draft) waiting for a person: "Held: 100 bps — lender draft
   *  (INQ-…), awaiting review". Never the row's value. */
  held?: string
}

/** The catalogue fields each Financing Summary row would show, for matching
 *  a held revision to the row it would fill. */
const FINANCING_FIELDS: Record<string, readonly string[]> = {
  'Loan Amount': ['loan_amount'],
  Position: ['position', 'lien_position'],
  'Loan Type': ['loan_type', 'product', 'capital_program'],
  'Rate Type': ['rate_type'],
  'Interest Rate': ['rate'],
  'Day Count': ['day_count'],
  'Index Rate Type': ['index'],
  Spread: ['spread'],
  Floor: ['floor'],
  'Payment Frequency': ['payment_frequency'],
  Amortization: ['amortization_type', 'amortization', 'amortization_months'],
  'Closing Date': ['closing_date'],
  'Loan Term': ['term_months'],
  Maturity: ['maturity_date'],
  'IO Period': ['io_months', 'io_period', 'interest_only_months'],
  'Extension Options': ['extension_count'],
  Extensions: ['extensions', 'extension_options'],
  'Extension Maturity': ['extension_maturity_date'],
  'Origination Fee': ['origination_fee_bps', 'origination_fee'],
  'Exit Fee': ['exit_fee_bps', 'exit_fee'],
  Prepayment: ['prepayment'],
  LTC: ['ltc'],
  'Debt Yield (In-Place)': ['debt_yield'],
  'Debt Yield (Stabilized)': ['debt_yield_stabilized'],
  'Value (Stabilized)': ['value_stabilized'],
  'LTV (Stabilized)': ['ltv_stabilized'],
}

/** Each row with the line of any value held for it (rule R5). A held value
 *  equal to what the row already shows adds nothing and is not repeated. */
function withHeld(rows: FinancingRow[], held: HeldRevision[]): FinancingRow[] {
  if (held.length === 0) return rows
  return rows.map((row) => {
    if (row.value === SENIOR_NOT_STATED) return row
    const entry = heldFor(held, FINANCING_FIELDS[row.label] ?? [])
    if (!entry) return row
    const monthsField = entry.fields.some((name) => name.endsWith('_months'))
    const shown = financingDisplay({ ...row, value: entry.incoming, format: monthsField ? 'months' : row.format })
    if (shown === '—' || shown === financingDisplay(row)) return row
    return { ...row, held: heldLine(entry, shown) }
  })
}

/** A financing row as the summary shows it. Missing values read as a dash. */
export function financingDisplay(row: FinancingRow): string {
  const value = row.value
  if (value === null || value === undefined || String(value).trim() === '') return '—'
  switch (row.format) {
    case 'money':
      return formatAmount(value)
    case 'percent': {
      const text = String(value)
      return text.includes('%') ? text : formatPct(value)
    }
    case 'points':
      return formatPoints(value)
    case 'months':
      return monthsLabel(value) ?? '—'
    case 'rate':
      return rateText(value) ?? '—'
    case 'bps':
      return bpsLabel(value) ?? '—'
    case 'spread':
      return spreadLabel(value) ?? '—'
    case 'date':
      return formatDate(value)
    case 'text':
      return String(value)
    default: {
      const _exhaustive: never = row.format
      return _exhaustive
    }
  }
}

export type SensitivityRow = {
  id: string
  label: string
  low: number | undefined
  base: number | undefined
  high: number | undefined
  result: number | undefined
  unit: string | null
  resultLabel: string | null
}

export type EconomicsUpdate = {
  id: string
  title: string
  detail: string
  at: string
}

function num(value: unknown): number | undefined {
  return asNumber(value)
}

function rawValue(raw: RawDealFile | undefined, key: string): unknown {
  if (!raw) return undefined
  const direct = raw[key]
  if (direct !== undefined && direct !== null && direct !== '') return direct
  const custom = raw.custom_fields
  if (custom && typeof custom === 'object') {
    const value = (custom as Record<string, unknown>)[key]
    if (value !== undefined && value !== null && value !== '') return value
  }
  return undefined
}

function objectValue(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}
}

function cashBlob(raw: RawDealFile | undefined): Record<string, unknown> {
  return objectValue(rawValue(raw, 'cash_flows'))
}

function marketBlob(raw: RawDealFile | undefined): Record<string, unknown> {
  return objectValue(rawValue(raw, 'market_data'))
}

/**
 * Cash-on-cash, one definition for every tab: the stated figure, else net cash
 * flow over total equity. Never yield on cost — the Capital Stack tab fell back
 * to `yield_on_cost` and showed Calloway at 5.29% while Economics showed the
 * stated 4.10% for the same deal.
 */
export function cashOnCashPct(
  stated: number | undefined,
  ncf: number | undefined,
  totalEquity: number | undefined,
): number | undefined {
  if (stated != null) return stated
  return ncf != null && totalEquity != null && totalEquity > 0 ? (ncf / totalEquity) * 100 : undefined
}

/** The named market source a market blob carries, or null when it names none. */
export function marketSourceName(market: Record<string, unknown>): string | null {
  for (const key of ['source', 'provider', 'marketSource']) {
    const value = market[key]
    if (typeof value === 'string' && value.trim()) return value.trim()
  }
  return null
}

/**
 * A cash-flow amount, in the DOLLARS every intake and seeded deal stores.
 * This used to read anything under 500 as $MM, so a $450 NOI showed as
 * $450,000,000 — a unit is never guessed from a figure's size (CMP-08).
 */
function moneyDollars(value: unknown): number | undefined {
  const parsed = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(parsed) || parsed === 0) return undefined
  return parsed
}

function present(value: unknown): boolean {
  return value !== null && value !== undefined && value !== ''
}

function text(value: unknown): string | null {
  if (!present(value)) return null
  const result = String(value).trim()
  return result && result !== '—' ? result : null
}

/** The first of `names` the deal holds, and which field it was: a row's value
 *  state is the state of the field that supplied it. */
function firstNamed(get: (name: string) => unknown, names: string[]): { value: unknown; fieldName?: string } {
  for (const name of names) {
    const value = get(name)
    if (present(value)) return { value, fieldName: name }
  }
  return { value: undefined, fieldName: undefined }
}

function readExpenses(cash: Record<string, unknown>): ExpenseSlice[] {
  const source = cash.expenses ?? cash.expenseBreakdown ?? cash.expense_breakdown
  if (!Array.isArray(source)) return []
  return source.flatMap((entry) => {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return []
    const row = entry as Record<string, unknown>
    const name = text(row.name ?? row.category ?? row.label)
    const amount = num(row.amount ?? row.value ?? row.total)
    return name && amount != null ? [{ name, amount: Math.abs(amount) }] : []
  })
}

function readProjections(cash: Record<string, unknown>): ProjectionRow[] {
  const source =
    cash.projections ??
    cash.years ??
    cash.annualCashFlows ??
    cash.annual_cash_flows ??
    cash.cashFlowProjection ??
    cash.cash_flow_projection
  if (!Array.isArray(source)) return []
  return source.flatMap((entry, index) => {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return []
    const row = entry as Record<string, unknown>
    const projection = {
      year: text(row.year ?? row.label ?? row.period) ?? `Year ${index + 1}`,
      egi: num(row.egi ?? row.grossRevenue ?? row.gross_revenue ?? row.effectiveGrossIncome),
      opex: num(row.opex ?? row.operatingExpenses ?? row.operating_expenses),
      noi: num(row.noi),
      cashFlow: num(row.cashFlow ?? row.ncf ?? row.cash_flow_after_debt ?? row.cfads),
      dscr: num(row.dscr),
    }
    return projection.egi != null || projection.opex != null || projection.noi != null || projection.cashFlow != null || projection.dscr != null
      ? [projection]
      : []
  })
}

function readScheduleSource(cash: Record<string, unknown>): ScheduleSource | null {
  const raw = cash.annual_cash_flows_source ?? cash.annualCashFlowsSource
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  const source = raw as Record<string, unknown>
  const workbook = text(source.workbook)
  const sheet = text(source.sheet)
  if (!workbook || !sheet) return null
  return {
    workbook,
    sheet,
    cadence: text(source.cadence) ?? 'unknown',
    lines: Array.isArray(source.lines) ? source.lines.map(String) : [],
    cells: (source.cells ?? {}) as Record<string, Record<string, string>>,
  }
}

function readUwGrid(cash: Record<string, unknown>): UwGrid | null {
  const raw = cash.uw_grid ?? cash.uwGrid
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  const source = raw as Record<string, unknown>
  const columns = (Array.isArray(source.columns) ? source.columns : []).flatMap((entry) => {
    if (!entry || typeof entry !== 'object') return []
    const row = entry as Record<string, unknown>
    const id = asUwColumnId(text(row.id))
    if (!id) return []
    return [{ id, label: text(row.label) ?? id }]
  })
  const lines = (Array.isArray(source.lines) ? source.lines : []).flatMap((entry) => {
    if (!entry || typeof entry !== 'object') return []
    const row = entry as Record<string, unknown>
    const label = text(row.label)
    if (!label) return []
    const values = objectValue(row.values)
    const cells = objectValue(row.cells)
    return [{
      label,
      key: text(row.key),
      values: {
        actuals: num(values.actuals) ?? null,
        sponsor: num(values.sponsor) ?? null,
        arep: num(values.arep) ?? null,
      },
      cells: {
        actuals: text(cells.actuals) ?? undefined,
        sponsor: text(cells.sponsor) ?? undefined,
        arep: text(cells.arep) ?? undefined,
      },
    }]
  })
  if (columns.length < 2 || lines.length === 0) return null
  return {
    workbook: text(source.workbook) ?? '',
    sheet: text(source.sheet) ?? '',
    columns,
    lines,
  }
}

function uwNumber(grid: UwGrid | null, key: string, column: UwColumnId = 'arep'): number | undefined {
  if (!grid) return undefined
  const line = grid.lines.find((item) => item.key === key)
  const value = line?.values[column] ?? line?.values.sponsor ?? line?.values.actuals
  return value ?? undefined
}

function readStatement(cash: Record<string, unknown>): SponsorStatement | null {
  const raw = cash.sponsor_statement ?? cash.sponsorStatement
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  const source = raw as Record<string, unknown>
  const lines = Array.isArray(source.lines) ? source.lines : []
  const parsed = lines.flatMap((entry) => {
    if (!entry || typeof entry !== 'object') return []
    const line = entry as Record<string, unknown>
    const label = text(line.label)
    if (!label) return []
    return [{
      label,
      key: text(line.key) ?? null,
      values: Array.isArray(line.values) ? line.values.map((v) => num(v) ?? null) : [],
      cells: Array.isArray(line.cells) ? line.cells.map(String) : [],
    }]
  })
  if (parsed.length === 0) return null
  return {
    workbook: text(source.workbook) ?? '',
    sheet: text(source.sheet) ?? '',
    cadence: text(source.cadence) ?? 'unknown',
    periods: Array.isArray(source.periods) ? source.periods.map(String) : [],
    lines: parsed,
    truncated: num(source.truncated) ?? undefined,
  }
}

function readTrend(cash: Record<string, unknown>): TrendPoint[] {
  const source = cash.trend ?? cash.incomeExpenseTrend ?? cash.income_expense_trend ?? cash.history
  if (!Array.isArray(source)) return []
  return source.flatMap((entry, index) => {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return []
    const row = entry as Record<string, unknown>
    const point: TrendPoint = {
      label: text(row.label ?? row.year ?? row.period) ?? `Year ${index + 1}`,
      egi: num(row.egi ?? row.grossRevenue ?? row.gross_revenue),
      opex: num(row.opex ?? row.operatingExpenses ?? row.operating_expenses),
      noi: num(row.noi),
    }
    return point.egi != null || point.opex != null || point.noi != null ? [point] : []
  })
}

function readDscrSeries(cash: Record<string, unknown>): DscrPoint[] {
  const source = cash.dscrSeries ?? cash.dscr_series ?? cash.dscr_history ?? cash.monthlyDscr
  if (!Array.isArray(source)) return []
  return source.flatMap((entry, index) => {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return []
    const row = entry as Record<string, unknown>
    const dscr = num(row.dscr ?? row.value)
    return dscr == null
      ? []
      : [{ label: text(row.label ?? row.month ?? row.year) ?? `Period ${index + 1}`, dscr }]
  })
}

function scenarioId(value: unknown): ScenarioId | null {
  const label = String(value ?? '').toLowerCase()
  if (/down|stress|bear/.test(label)) return 'downside'
  if (/up|optim|bull/.test(label)) return 'upside'
  if (/base|current|underwrit/.test(label)) return 'base'
  return null
}

function scenarioRecords(source: unknown): Record<string, unknown>[] {
  if (Array.isArray(source)) {
    return source.filter(
      (entry): entry is Record<string, unknown> => Boolean(entry) && typeof entry === 'object' && !Array.isArray(entry),
    )
  }
  if (!source || typeof source !== 'object') return []
  return Object.entries(source as Record<string, unknown>).flatMap(([key, value]) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return []
    return [{ scenario: key, ...(value as Record<string, unknown>) }]
  })
}

function readScenarios(
  raw: RawDealFile | undefined,
  cash: Record<string, unknown>,
): Partial<Record<ScenarioId, ScenarioMetrics>> {
  const source =
    cash.scenarios ??
    cash.scenarioAnalysis ??
    cash.scenario_analysis ??
    rawValue(raw, 'underwriting_scenarios') ??
    rawValue(raw, 'scenario_analysis')
  const result: Partial<Record<ScenarioId, ScenarioMetrics>> = {}
  for (const row of scenarioRecords(source)) {
    const metrics = objectValue(row.metrics)
    const merged = { ...row, ...metrics }
    const id = scenarioId(row.id ?? row.scenario ?? row.name ?? row.label ?? row.case)
    if (!id) continue
    // Each figure keeps the basis the scenario stated for it. A bare "noi" or
    // "dscr" is not a stabilized one, and a bare "value" (an exit-cap value on
    // the Calloway control deal) is not an as-is one (C09).
    const valueAsIs = moneyDollars(merged.asIsValue ?? merged.as_is_value ?? merged.valueAsIs ?? merged.value_as_is)
    const valueStabilized = moneyDollars(
      merged.stabilizedValue ?? merged.stabilized_value ?? merged.valueStabilized ?? merged.value_stabilized,
    )
    const valueUnstated = moneyDollars(merged.estimatedValue ?? merged.estimated_value ?? merged.propertyValue ?? merged.value)
    const valueBasis: ScenarioValueBasis | undefined =
      valueAsIs != null ? 'as_is' : valueStabilized != null ? 'stabilized' : valueUnstated != null ? 'unstated' : undefined
    result[id] = {
      id,
      label:
        text(row.label ?? row.name ?? row.case) ??
        (id === 'base' ? 'Base Case' : id === 'downside' ? 'Downside' : 'Upside'),
      noi: moneyDollars(merged.noiStabilized ?? merged.noi_stabilized),
      noiUnqualified: moneyDollars(merged.noi),
      value: valueAsIs ?? valueStabilized ?? valueUnstated,
      valueBasis,
      ltv: num(merged.ltv),
      dscr: num(merged.dscrStabilized ?? merged.dscr_stabilized),
      dscrUnqualified: num(merged.dscr),
      cashOnCash: num(merged.cashOnCash ?? merged.cash_on_cash),
      irr: num(merged.leveredIrr ?? merged.levered_irr ?? merged.irr),
      note: text(row.note ?? row.description),
    }
  }
  return result
}

function readSensitivity(
  raw: RawDealFile | undefined,
  cash: Record<string, unknown>,
): SensitivityRow[] {
  const source =
    cash.sensitivity ??
    cash.sensitivityAnalysis ??
    cash.sensitivity_analysis ??
    rawValue(raw, 'sensitivity_analysis')
  const records = scenarioRecords(source)
  return records.flatMap((row, index) => {
    const values = Array.isArray(row.values) ? row.values : []
    const label = text(row.label ?? row.metric ?? row.name ?? row.scenario)
    const low = num(row.low ?? row.min ?? row.minimum ?? values[0])
    const base = num(row.base ?? row.current ?? row.value ?? values[1])
    const high = num(row.high ?? row.max ?? row.maximum ?? values[2])
    const result = num(row.result ?? row.output ?? row.irr)
    if (!label || (low == null && base == null && high == null && result == null)) return []
    return [{
      id: text(row.id) ?? `${label}-${index}`,
      label,
      low,
      base,
      high,
      result,
      unit: text(row.unit),
      resultLabel: text(row.resultLabel ?? row.result_label ?? row.outputLabel),
    }]
  })
}

function humanize(value: string): string {
  return value.replace(/[_-]+/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())
}

function eventDetail(event: LoanActivityEvent): string {
  const details = event.details ?? {}
  return (
    text(details.message ?? details.detail ?? details.filename ?? details.documentName) ??
    text(event.userEmail) ??
    ''
  )
}

export function buildEconomicsModel(input: {
  get: (name: string) => unknown
  raw: RawDealFile | undefined
  activity: LoanActivityEvent[]
  notes: { id: string; body: string; author: string; createdAt: string }[]
  /** The property master's operating history, when this deal links to one.
   *  It is a measured series the asset-management side maintains, so it fills
   *  the income/expense/NOI trend for a deal whose own record carries none —
   *  but only as a gap-fill: a series stated on the deal always wins. */
  operatingHistory?: Record<string, unknown>[] | null
}) {
  const { get, raw, activity, notes } = input
  const cash = cashBlob(raw)
  const market = marketBlob(raw)

  const loanAmount = num(get('loan_amount'))
  const propertyValue = num(get('property_value'))
  const ltv = num(get('ltv'))
  const noi = num(get('noi')) ?? moneyDollars(cash.prospectiveNOI) ?? moneyDollars(cash.historicNOI)
  const noiStabilized = num(get('noi_stabilized'))
  const dscr = num(get('dscr'))
  const annualDebtService = num(get('annual_debt_service'))
  const grossRevenue = num(get('gross_revenue'))
  const opex = num(get('operating_expenses'))
  const capRate = num(get('cap_rate'))
  const exitCap = num(get('exit_cap'))
  // A market cap rate is market data: shown only when the market blob names
  // the source it came from. Promotion used to copy the package's own cap rate
  // into `market_data.capRate`, so Gateway read "In-Place 5.75% / Stabilized
  // 5.75% / Market 5.75%" — one broker figure three times, once as market data.
  const marketCap = marketSourceName(market)
    ? num(market.cap_rate) ?? num(market.capRate)
    : undefined
  const leveredIrr = num(get('levered_irr'))
  const unleveredIrr = num(get('unlevered_irr'))
  const equityMultiple = num(get('equity_multiple'))
  const totalEquity = num(get('total_equity'))
  const cashOnCash = cashOnCashPct(num(get('cash_on_cash')), num(get('ncf')), totalEquity)
  const stabilizedCashOnCash =
    num(get('cash_on_cash_stabilized')) ??
    (num(get('ncf_stabilized')) != null && totalEquity != null && totalEquity > 0
      ? (num(get('ncf_stabilized'))! / totalEquity) * 100
      : undefined)
  const dscrStabilized =
    num(get('dscr_stabilized')) ??
    (noiStabilized != null && annualDebtService != null && annualDebtService > 0
      ? noiStabilized / annualDebtService
      : undefined)

  const uwGrid = readUwGrid(cash)
  const opexStated = opex ?? uwNumber(uwGrid, 'opex')
  const noiStated = noi ?? uwNumber(uwGrid, 'noi')
  const scheduledGrossIncome =
    num(get('scheduled_gross_income')) ??
    num(cash.scheduledGrossIncome ?? cash.scheduled_gross_income ?? cash.grossPotentialRent) ??
    uwNumber(uwGrid, 'gpr')
  const vacancyLoss =
    num(cash.vacancyLoss ?? cash.vacancy_loss ?? cash.vacancy) ??
    uwNumber(uwGrid, 'vacancy')
  const lossToLease = num(cash.lossToLease ?? cash.loss_to_lease ?? cash.ltl) ?? uwNumber(uwGrid, 'ltl')
  const concessions = num(cash.concessions) ?? uwNumber(uwGrid, 'concessions')
  const badDebt = num(cash.badDebt ?? cash.bad_debt) ?? uwNumber(uwGrid, 'bad_debt')
  const otherIncome =
    num(get('other_income')) ??
    num(cash.otherIncome ?? cash.other_income) ??
    uwNumber(uwGrid, 'other_income')
  const vacancy = vacancyLoss
  const statedEgi =
    num(cash.effectiveGrossIncome ?? cash.egi ?? cash.effective_gross_income) ??
    uwNumber(uwGrid, 'egi') ??
    grossRevenue
  // A computed EGI is Derived and names its formula (C05). Every deduction the
  // record states comes off — the lines above it list loss to lease,
  // concessions and bad debt, and an EGI that ignored them did not add up to
  // what the page printed. With no vacancy stated it assumes $0: Suggested.
  const egiDeductions = (
    [
      ['vacancy', vacancy],
      ['loss to lease', lossToLease],
      ['concessions', concessions],
      ['bad debt', badDebt],
    ] as [string, number | undefined][]
  ).filter((entry): entry is [string, number] => entry[1] != null)
  const egiFormula =
    statedEgi == null && scheduledGrossIncome != null
      ? ['Gross potential rent', otherIncome != null ? '+ other income' : null, ...egiDeductions.map(([name]) => `− ${name}`)]
          .filter(Boolean)
          .join(' ')
      : undefined
  const egi =
    statedEgi ??
    (scheduledGrossIncome != null
      ? scheduledGrossIncome + (otherIncome ?? 0) - egiDeductions.reduce((sum, [, amount]) => sum + Math.abs(amount), 0)
      : undefined)
  const egiAssumed =
    egiFormula && vacancy == null
      ? `Suggested: no vacancy is stated, so this assumes vacancy of $0 — ${egiFormula.toLowerCase()}`
      : undefined
  const egiDerived = egiFormula && !egiAssumed ? egiFormula : undefined
  // The catalogue field is `reserve` (FF&E / Replacement Reserve). Reading only
  // 'reserves' / 'replacement_reserve' dropped The Ardsley's stated $1,156,000
  // and overstated NCF by the whole reserve (OVR-10 / ECON-14).
  const reserves =
    num(get('reserve')) ?? num(get('reserves')) ?? num(get('replacement_reserve')) ?? num(cash.reserves)
  const statedNcfBefore =
    num(get('ncf_before_debt_service')) ??
    num(cash.netCashFlowBeforeDebtService ?? cash.net_cash_flow_before_debt_service)
  const netCashBeforeDebt =
    statedNcfBefore ?? (noi != null ? noi - Math.abs(reserves ?? 0) : undefined)
  // NOI less a reserve nobody stated is NOI less an ASSUMED $0 — a Suggested
  // figure, never presented as the deal's net cash flow (ECON-14).
  const reserveAssumed =
    statedNcfBefore == null && netCashBeforeDebt != null && reserves == null
      ? 'Suggested: no reserve is stated, so this assumes reserves of $0 — NOI less $0'
      : undefined
  const statedNcfAfter =
    num(get('ncf')) ??
    num(cash.netCashFlowAfterDebtService ?? cash.net_cash_flow_after_debt_service)
  const netCashAfterDebt =
    statedNcfAfter ??
    (netCashBeforeDebt != null && annualDebtService != null
      ? netCashBeforeDebt - Math.abs(annualDebtService)
      : undefined)
  const afterAssumed = statedNcfAfter == null && netCashAfterDebt != null ? reserveAssumed : undefined
  // Computed here, from the lines above: Derived with the formula (C05).
  const beforeDerived =
    statedNcfBefore == null && netCashBeforeDebt != null && !reserveAssumed ? 'NOI − reserves' : undefined
  const afterDerived =
    statedNcfAfter == null && netCashAfterDebt != null && !afterAssumed
      ? 'NCF before debt service − debt service'
      : undefined
  // The catalogue field each stated line shows, so its own lineage state
  // (a derived opex, a suggested reserve) is drawn beside it.
  const reserveField =
    num(get('reserve')) != null ? 'reserve' : num(get('reserves')) != null ? 'reserves' : num(get('replacement_reserve')) != null ? 'replacement_reserve' : undefined

  // A hotel sells rooms by the night: it has no rent roll, so no GPR, loss to
  // lease or concessions, and states its economics as occupancy, ADR, RevPAR
  // and a departmental P&L. The Ardsley's Economics tab listed the rent-roll
  // lines blank and showed none of its captured hotel figures (PROP-16).
  const lodging = isLodging(text(get('asset_type')) ?? text(raw?.asset_type))
  const hotelStats: HotelStat[] | null = lodging
    ? [
        { label: keysLabel(get('unit_of_measure')), value: num(get('units'))?.toLocaleString('en-US') ?? '—', fieldName: 'units' },
        { label: 'Occupancy', value: formatPoints(get('occupancy')), fieldName: 'occupancy' },
        { label: 'ADR', value: formatAmount(num(get('adr'))), fieldName: 'adr' },
        { label: 'RevPAR', value: formatAmount(num(get('revpar'))), fieldName: 'revpar' },
        { label: 'Room nights', value: num(get('room_nights'))?.toLocaleString('en-US') ?? '—', fieldName: 'room_nights' },
      ]
    : null
  const outflow = (value: number | undefined) => (value != null ? -Math.abs(value) : undefined)

  const tail: CashFlowLine[] = [
    { label: 'Total Operating Expenses', value: opexStated != null ? -Math.abs(opexStated) : undefined, expense: true, fieldName: opex != null ? 'operating_expenses' : undefined },
    { label: 'Net Operating Income', value: noiStated, fieldName: noi != null && num(get('noi')) != null ? 'noi' : undefined },
    { label: lodging ? 'FF&E Reserve' : 'Reserves', value: reserves != null ? -Math.abs(reserves) : undefined, expense: true, fieldName: reserveField },
    {
      label: 'Net Cash Flow Before Debt Service',
      value: netCashBeforeDebt,
      suggested: reserveAssumed,
      derived: beforeDerived,
      fieldName: num(get('ncf_before_debt_service')) != null ? 'ncf_before_debt_service' : undefined,
      inputs: ['noi', 'reserve'],
    },
    { label: 'Debt Service', value: annualDebtService != null ? -Math.abs(annualDebtService) : undefined, expense: true, fieldName: 'annual_debt_service' },
    {
      label: 'Net Cash Flow After Debt Service',
      value: netCashAfterDebt,
      emphasize: true,
      suggested: afterAssumed,
      derived: afterDerived,
      fieldName: num(get('ncf')) != null ? 'ncf' : undefined,
      inputs: ['noi', 'reserve', 'annual_debt_service'],
    },
  ]
  const cashFlowLines: CashFlowLine[] = lodging
    ? [
        {
          label: 'Total Revenue',
          value: grossRevenue ?? egi,
          fieldName: grossRevenue != null ? 'gross_revenue' : undefined,
          derived: grossRevenue == null ? egiDerived : undefined,
          suggested: grossRevenue == null ? egiAssumed : undefined,
        },
        { label: 'Departmental Profit', value: num(get('departmental_profit')), fieldName: 'departmental_profit' },
        { label: 'Undistributed Expenses', value: outflow(num(get('undistributed_expenses'))), expense: true, fieldName: 'undistributed_expenses' },
        { label: 'Gross Operating Profit', value: num(get('gop')), fieldName: 'gop' },
        { label: 'Fixed Expenses', value: outflow(num(get('fixed_expenses'))), expense: true, fieldName: 'fixed_expenses' },
        ...tail,
      ]
    : [
        { label: 'Gross Potential Rent', value: scheduledGrossIncome, fieldName: num(get('scheduled_gross_income')) != null ? 'scheduled_gross_income' : undefined },
        { label: 'Vacancy', value: vacancyLoss != null ? -Math.abs(vacancyLoss) : undefined, expense: true },
        { label: 'Loss to Lease', value: lossToLease != null ? -Math.abs(lossToLease) : undefined, expense: true },
        { label: 'Concessions', value: concessions != null ? -Math.abs(concessions) : undefined, expense: true },
        { label: 'Bad Debt', value: badDebt != null ? -Math.abs(badDebt) : undefined, expense: true },
        { label: 'Other Income', value: otherIncome, fieldName: num(get('other_income')) != null ? 'other_income' : undefined },
        { label: 'Effective Gross Income', value: egi, derived: egiDerived, suggested: egiAssumed },
        ...tail,
      ]

  let expenses = readExpenses(cash)
  if (expenses.length === 0) {
    // Operating-expense lines only: the FF&E reserve sits BELOW NOI in the
    // cash flow above, and listing it here made the reserve alone read as the
    // property's total expenses ($1.2M against $21.3M stated — PKG-08).
    expenses = [
      ['Fixed Expenses', get('fixed_expenses')],
      ['Undistributed Expenses', get('undistributed_expenses')],
    ].flatMap(([name, value]) => {
      const amount = num(value)
      return amount == null ? [] : [{ name: String(name), amount: Math.abs(amount) }]
    })
    const itemised = expenses.reduce((sum, expense) => sum + expense.amount, 0)
    if (opex != null && Math.abs(opex) > itemised) {
      expenses = [
        ...expenses,
        { name: expenses.length ? 'Other Operating Expenses' : 'Operating Expenses', amount: Math.abs(opex) - itemised },
      ]
    }
  }

  const projections = readProjections(cash)
  const storedTrend = readTrend(cash)
  const masterTrend = readTrend({ trend: input.operatingHistory ?? [] })
  const trend = storedTrend.length > 0
    ? storedTrend
    : projections.length > 0
      ? projections.flatMap((row) =>
          row.egi != null || row.opex != null || row.noi != null
            ? [{ label: row.year, egi: row.egi, opex: row.opex, noi: row.noi }]
            : [],
        )
      : masterTrend
  const storedDscr = readDscrSeries(cash)
  const dscrSeries = storedDscr.length > 0
    ? storedDscr
    : projections.flatMap((row) => row.dscr == null ? [] : [{ label: row.year, dscr: row.dscr }])

  // Same basis on both legs: a stabilized NOI over the STABILIZED value. The
  // fallback divided it by the as-is value, which is how The Marlowe showed a
  // 7.03% "stabilized" cap rate ($2.65M / $37.7M as-is).
  const valueStabilized = num(get('value_stabilized'))
  const capStabilizedStated = num(get('cap_rate_stabilized'))
  const capStabilized =
    capStabilizedStated ??
    (noiStabilized != null && valueStabilized != null && valueStabilized > 0
      ? (noiStabilized / valueStabilized) * 100
      : undefined)
  // The page's own arithmetic, when no stabilized cap rate is held: it reads
  // Derived with its formula, never as a stated figure.
  const capStabilizedDerived =
    capStabilizedStated == null && capStabilized != null ? 'stabilized NOI / stabilized value' : undefined

  // Catalogue names first (term_months, io_months, extensions,
  // origination_fee_bps, exit_fee_bps); the legacy keys only the seeded
  // control deal carries are a fallback, never the primary read.
  const debtYieldUnreadable = seniorAheadUnstated(get, raw?.capital_stack)
  const debtYield = debtYieldUnreadable ? undefined : num(get('debt_yield'))
  const ltc = num(get('ltc'))
  const loanType = productField(get) ?? { value: undefined, fieldName: undefined }
  const ioPeriod = firstNamed(get, ['io_months', 'io_period', 'interest_only_months'])
  const extensions = firstNamed(get, ['extensions', 'extension_options'])
  const originationFee = firstNamed(get, ['origination_fee_bps', 'origination_fee'])
  const exitFee = firstNamed(get, ['exit_fee_bps', 'exit_fee'])
  const financing: FinancingRow[] = [
    { label: 'Loan Amount', value: loanAmount, format: 'money', fieldName: 'loan_amount' },
    { label: 'Position', value: positionLabel(get), format: 'text', fieldName: firstNamed(get, ['position', 'lien_position']).fieldName },
    { label: 'Loan Type', value: loanType.value, format: 'text', fieldName: loanType.fieldName },
    { label: 'Rate Type', value: get('rate_type'), format: 'text', fieldName: 'rate_type' },
    { label: 'Interest Rate', value: get('rate'), format: 'rate', fieldName: 'rate' },
    // The accrual basis interest-only coverage is computed on (WP-G): Actual/360
    // unless the deal says otherwise — a default is recorded as a Suggestion.
    { label: 'Day Count', value: get('day_count'), format: 'text', fieldName: 'day_count' },
    // The catalogue's label: the server's conflict and validation texts name
    // the field "Index Rate Type", and this row is where it is edited (nd3-45).
    { label: 'Index Rate Type', value: get('index'), format: 'text', fieldName: 'index' },
    { label: 'Spread', value: get('spread'), format: 'spread', fieldName: 'spread' },
    { label: 'Floor', value: num(get('floor')), format: 'points', fieldName: 'floor' },
    { label: 'Payment Frequency', value: get('payment_frequency'), format: 'text', fieldName: 'payment_frequency' },
    {
      label: 'Amortization',
      value: amortizationLabel(get),
      format: 'text',
      fieldName: firstNamed(get, ['amortization_type', 'amortization', 'amortization_months']).fieldName,
    },
    // The closing date is the desk's record of the day the loan closed (not the
    // target close). Maturity and Extension Maturity are counted from it: stated
    // when a package or a person states them, otherwise derived by the platform
    // (closing date + initial term; + every extension option), blank until the
    // closing date and the term are on the deal.
    { label: 'Closing Date', value: get('closing_date'), format: 'date', fieldName: 'closing_date' },
    { label: 'Loan Term', value: get('term_months'), format: 'months', fieldName: 'term_months' },
    { label: 'Maturity', value: get('maturity_date'), format: 'date', fieldName: 'maturity_date' },
    { label: 'IO Period', value: ioPeriod.value, format: 'months', fieldName: ioPeriod.fieldName },
    { label: 'Extension Options', value: get('extension_count'), format: 'text', fieldName: 'extension_count' },
    { label: 'Extensions', value: extensions.value, format: 'months', fieldName: extensions.fieldName },
    { label: 'Extension Maturity', value: get('extension_maturity_date'), format: 'date', fieldName: 'extension_maturity_date' },
    { label: 'Origination Fee', value: originationFee.value, format: 'bps', fieldName: originationFee.fieldName },
    { label: 'Exit Fee', value: exitFee.value, format: 'bps', fieldName: exitFee.fieldName },
    { label: 'Prepayment', value: get('prepayment'), format: 'text', fieldName: 'prepayment' },
    { label: 'LTC', value: ltc, format: 'points', fieldName: 'ltc' },
    {
      label: 'Debt Yield (In-Place)',
      value: debtYieldUnreadable ? SENIOR_NOT_STATED : debtYield,
      format: debtYieldUnreadable ? 'text' : 'points',
      fieldName: debtYieldUnreadable ? undefined : 'debt_yield',
    },
    {
      label: 'Debt Yield (Stabilized)',
      value: debtYieldUnreadable ? SENIOR_NOT_STATED : num(get('debt_yield_stabilized')),
      format: debtYieldUnreadable ? 'text' : 'points',
      fieldName: debtYieldUnreadable ? undefined : 'debt_yield_stabilized',
    },
    { label: 'Value (Stabilized)', value: num(get('value_stabilized')), format: 'money', fieldName: 'value_stabilized' },
    { label: 'LTV (Stabilized)', value: num(get('ltv_stabilized')), format: 'points', fieldName: 'ltv_stabilized' },
  ]
  const financingWithHeld = withHeld(financing, heldRevisions(raw))

  const savedScenarios = readScenarios(raw, cash)
  if (!savedScenarios.base) {
    // The scenario rows are headed NOI (Stabilized) and DSCR (Stabilized).
    // Never fall back across the in-place / stabilized axis to fill them: Senate
    // Row's "Recorded Case" showed its in-place $5.18M and 1.80x under those
    // headings with no stabilized case underwritten at all.
    const hasRecordedBase = [noiStabilized, propertyValue, ltv, dscrStabilized, cashOnCash, leveredIrr]
      .some((value) => value != null)
    if (hasRecordedBase) {
      savedScenarios.base = {
        id: 'base',
        label: 'Recorded Case',
        noi: noiStabilized,
        value: propertyValue,
        // property_value is the deal's as-is value (the Value (As-Is) tile).
        valueBasis: propertyValue != null ? 'as_is' : undefined,
        ltv,
        dscr: dscrStabilized,
        cashOnCash,
        irr: leveredIrr,
        note: 'Current recorded economics; no separate saved base scenario was found.',
        loanAmount,
      }
    }
  }
  const scenarios: Record<ScenarioId, ScenarioMetrics | null> = {
    base: savedScenarios.base ?? null,
    downside: savedScenarios.downside ?? null,
    upside: savedScenarios.upside ?? null,
  }
  const savedScenarioCount = Object.values(scenarios).filter(Boolean).length
  const scenarioDisclosure =
    savedScenarioCount === 0
      ? 'No underwriting scenarios are stored on this deal.'
      : scenarios.downside == null && scenarios.upside == null
        ? 'Only the recorded current/base case is available. Downside and upside cases were not inferred.'
        : 'Only saved scenario cases are shown; unavailable cases remain blank.'

  const updates: EconomicsUpdate[] = []
  for (const note of notes.slice(0, 8)) {
    updates.push({
      id: `note-${note.id}`,
      title: 'Note added',
      detail: [text(note.body), text(note.author)].filter(Boolean).join(' · '),
      at: note.createdAt,
    })
  }
  for (const event of activity.slice(0, 12)) {
    updates.push({
      id: `activity-${event.id}`,
      title: humanize(event.eventType || 'Activity'),
      detail: eventDetail(event),
      at: event.createdAt ?? '',
    })
  }
  updates.sort((left, right) => Date.parse(right.at || '') - Date.parse(left.at || ''))

  return {
    kpis: {
      loanAmount,
      propertyValue,
      ltv,
      ltc,
      debtYield,
      debtYieldStabilized: debtYieldUnreadable ? undefined : num(get('debt_yield_stabilized')),
      debtYieldUnreadable,
      noi,
      noiStabilized,
      dscr,
      dscrStabilized,
      cashOnCash,
      leveredIrr,
    },
    cashFlowLines,
    hotelStats,
    expenses,
    expenseTotal: expenses.reduce((sum, expense) => sum + expense.amount, 0) || opex,
    dscrSeries,
    trend,
    projections,
    projectionSource: readScheduleSource(cash),
    sponsorStatement: readStatement(cash),
    uwGrid,
    // The catalogue's cap rate is unqualified. It is the in-place one only
    // beside an in-place NOI; a broker's bare "Cap Rate: 5.75%" on a deal
    // that states only a stabilized NOI is not (nd-007, Gateway).
    capRateLabel: noi != null ? 'Cap Rate (In-Place)' : 'Cap Rate (as stated)',
    capRates: {
      inplace: capRate,
      stabilized: capStabilized,
      market: marketCap,
      terminal: exitCap,
    },
    /** The formula of a stabilized cap rate this model computed because the
     *  deal holds none; undefined when the figure is the deal's own field. */
    capStabilizedDerived,
    financing: financingWithHeld,
    // Interest-only coverage on the lender's Actual/360 basis, beside the
    // broker-stated DSCR and (for a hotel) after the entered FF&E reserve —
    // each with its basis, a dash where its inputs are missing (WP-G).
    coverage: buildCoverageRows({
      get,
      lodging,
      derivedFields: Array.isArray(raw?.derived_fields) ? (raw.derived_fields as string[]) : [],
    }),
    scenarios,
    scenarioDisclosure,
    sensitivity: readSensitivity(raw, cash),
    returns: {
      cashOnCash,
      cashOnCashStabilized: stabilizedCashOnCash,
      leveredIrr,
      unleveredIrr,
      equityMultiple,
      capInplace: capRate,
      capStabilized,
    },
    updates: updates.slice(0, 8),
  }
}

export type EconomicsModel = ReturnType<typeof buildEconomicsModel>
